// src/pages/admin/DistrictMasterManager.js
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Edit2,
  Filter,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  X,
} from "react-feather";
import { toast } from "react-toastify";

import {
  fetchDistrictListThunk,
  createDistrictThunk,
  updateDistrictThunk,
  deleteDistrictThunk,
  openDtModal,
  closeDtModal,
  setDtForm,
  resetDtForm,
} from "../../../store/slices/districtTalukMasterSlice";

import "../../../css/master-common.css";

const e = React.createElement;

const ITEMS_PER_PAGE = 10;

const SORT_OPTIONS = [
  { value: "dist_name_asc", label: "District Name (A-Z)" },
  { value: "dist_name_desc", label: "District Name (Z-A)" },
  { value: "status_active_first", label: "Status (Active first)" },
  { value: "status_inactive_first", label: "Status (Inactive first)" },
];

const Pill = ({ children }) => e(Badge, { className: "mm-pill" }, children);

const StatusPill = ({ status }) => {
  const isActive = Number(status) === 1;
  return e(Pill, null, isActive ? "Active" : "Inactive");
};

export default function DistrictMasterManager() {
  const dispatch = useDispatch();

  const {
    districts,
    districtLoading,
    districtSaving,
    districtDeleting,
    modalOpen,
    modalMode,
    modalType,
    form,
    error,
  } = useSelector((s) => s.districtTalukMaster);

  // local ui
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("dist_name_asc");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchDistrictListThunk());
  }, [dispatch]);

  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((x) => x.value === sortBy)?.label || "Sort",
    [sortBy]
  );

  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    let rows = Array.isArray(districts) ? [...districts] : [];

    if (q) {
      rows = rows.filter((r) => String(r.dist_name || "").toLowerCase().includes(q));
    }

    rows.sort((a, b) => {
      const aName = String(a.dist_name || "");
      const bName = String(b.dist_name || "");
      const aSt = Number(a.status) === 1 ? 1 : 0;
      const bSt = Number(b.status) === 1 ? 1 : 0;

      switch (sortBy) {
        case "dist_name_asc":
          return aName.localeCompare(bName);
        case "dist_name_desc":
          return bName.localeCompare(aName);
        case "status_active_first":
          return bSt - aSt; // active first (1 then 0)
        case "status_inactive_first":
          return aSt - bSt; // inactive first (0 then 1)
        default:
          return aName.localeCompare(bName);
      }
    });

    return rows;
  }, [districts, search, sortBy]);

  // pagination
  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy]);

  const getPaginationItems = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 576;
    const maxVisiblePages = isMobile ? 3 : 5;
    const goTo = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    items.push(
      e(
        Pagination.Prev,
        { key: "prev", onClick: () => goTo(currentPage - 1), disabled: currentPage === 1 },
        e(ChevronLeft, { size: 16, className: "me-1" }),
        "Prev"
      )
    );

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(e(Pagination.Item, { key: 1, onClick: () => goTo(1) }, "1"));
      if (!isMobile && startPage > 2) items.push(e(Pagination.Ellipsis, { key: "es" }));
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        e(Pagination.Item, { key: i, active: i === currentPage, onClick: () => goTo(i) }, String(i))
      );
    }

    if (endPage < totalPages) {
      if (!isMobile && endPage < totalPages - 1) items.push(e(Pagination.Ellipsis, { key: "ee" }));
      items.push(
        e(Pagination.Item, { key: totalPages, onClick: () => goTo(totalPages) }, String(totalPages))
      );
    }

    items.push(
      e(
        Pagination.Next,
        { key: "next", onClick: () => goTo(currentPage + 1), disabled: currentPage === totalPages },
        "Next",
        e(ChevronRight, { size: 16, className: "ms-1" })
      )
    );

    return items;
  };

  const openAdd = () => {
    dispatch(resetDtForm());
    dispatch(openDtModal({ type: "district", mode: "add" }));
  };

  const openEdit = (r) => {
    dispatch(
      setDtForm({
        dist_id: r.dist_id,
        dist_name: r.dist_name,
        status: Number(r.status) === 1 ? 1 : 0,
      })
    );
    dispatch(openDtModal({ type: "district", mode: "edit" }));
  };

  const onSave = async () => {
    if (modalType !== "district") return;

    const payload = {
      dist_id: form.dist_id,
      dist_name: String(form.dist_name || "").trim(),
      status: Number(form.status) === 1 ? 1 : 0,
    };

    if (!payload.dist_name) {
      toast.warning("District name required");
      return;
    }

    const res =
      modalMode === "add"
        ? await dispatch(createDistrictThunk(payload))
        : await dispatch(updateDistrictThunk(payload));

    if (res?.meta?.requestStatus === "fulfilled") {
      toast.success(modalMode === "add" ? "District created ✅" : "District updated ✅");
      dispatch(closeDtModal());
      dispatch(fetchDistrictListThunk());
    } else {
      toast.error(res?.payload || "Failed to save");
    }
  };

  const onDelete = (r) => {
    toast.info(
      ({ closeToast }) =>
        e(
          "div",
          null,
          e(
            "div",
            { className: "fw-semibold mb-1" },
            `Delete District "${String(r.dist_name || "")}"?`
          ),
          e(
            "div",
            { className: "d-flex gap-2 mt-2" },
            e(
              "button",
              {
                className: "btn btn-sm btn-danger",
                onClick: async () => {
                  const res = await dispatch(deleteDistrictThunk(r.dist_id));
                  if (res?.meta?.requestStatus === "fulfilled") {
                    toast.success("District deleted");
                    dispatch(fetchDistrictListThunk());
                  } else {
                    toast.error(res?.payload || "Failed to delete district");
                  }
                  closeToast();
                },
              },
              districtDeleting ? "..." : "Delete"
            ),
            e(
              "button",
              { className: "btn btn-sm btn-secondary", onClick: closeToast },
              "Cancel"
            )
          )
        ),
      { autoClose: false }
    );
  };

  const clearSearch = () => setSearch("");

  // ---------------------------
  // RENDER (NO JSX)
  // ---------------------------
  return e(
    "div",
    { className: "container-fluid px-3 py-4 mm-page" },

    // Header
    e(
      "div",
      { className: "d-flex justify-content-between align-items-center mb-3" },
      e(
        "div",
        null,
        e("h4", { className: "mb-1 fw-bold mm-title" }, "District Master"),
        // e("div", { className: "mm-subtitle" }, "Create / Edit / Delete Districts")
      ),
      e(
        "div",
        { className: "d-flex gap-2" },
        e(
          Button,
          {
            variant: "light",
            className: "mm-cancel-btn d-flex align-items-center gap-2",
            onClick: () => dispatch(fetchDistrictListThunk()),
            disabled: districtLoading,
            title: "Refresh",
          },
          e(RefreshCcw, { size: 16 }),
          districtLoading ? "Refreshing..." : "Refresh"
        ),
        e(
          Button,
          {
            onClick: openAdd,
            className: "mm-primary-btn d-flex align-items-center gap-2",
            title: "Add District",
          },
          e(Plus, { size: 18 }),
          "Add District"
        )
      )
    ),

    // Error
    error
      ? e(
          Alert,
          {
            variant: "danger",
            className: "mm-card-soft mb-3",
            dismissible: true,
            onClose: () => {}, // slice doesn't have clear error action here
          },
          e("div", { className: "fw-bold mb-1" }, "Error"),
          e("div", null, String(error))
        )
      : null,

    // Filters
    e(
      Card,
      { className: "mm-card-soft mb-3" },
      e(
        Card.Body,
        { className: "p-3" },
        e(
          Row,
          { className: "g-3 align-items-end" },

          // Search
          e(
            Col,
            { md: 6 },
            e(Form.Label, { className: "fw-bold mb-1" }, "Search"),
            e(
              InputGroup,
              null,
              e(
                InputGroup.Text,
                { className: "mm-inputgroup-text" },
                e(Search, { size: 18, className: "text-muted" })
              ),
              e(FormControl, {
                className: "mm-input mm-inputgroup-control",
                placeholder: "Search district...",
                value: search,
                onChange: (ev) => setSearch(ev.target.value),
              }),
              search
                ? e(
                    Button,
                    { variant: "light", className: "mm-clear-btn", onClick: clearSearch },
                    e(X, { size: 16 })
                  )
                : null
            )
          ),

          // Sort
          e(
            Col,
            { md: 3 },
            e(Form.Label, { className: "fw-bold mb-1" }, "Sort"),
            e(
              Dropdown,
              { align: "end" },
              e(
                Dropdown.Toggle,
                {
                  className:
                    "mm-dropdown-btn w-100 d-flex align-items-center justify-content-between",
                },
                e(
                  "span",
                  { className: "d-flex align-items-center gap-2" },
                  e(Filter, { size: 16 }),
                  sortLabel
                ),
                e(ChevronDown, { size: 16 })
              ),
              e(
                Dropdown.Menu,
                { className: "mm-dd-menu w-100" },
                ...SORT_OPTIONS.map((opt) =>
                  e(
                    Dropdown.Item,
                    {
                      key: opt.value,
                      active: sortBy === opt.value,
                      onClick: () => setSortBy(opt.value),
                    },
                    opt.label
                  )
                )
              )
            )
          ),

          // Filter (kept for layout parity)
          e(
            Col,
            { md: 3 },
            e(Form.Label, { className: "fw-bold mb-1" }, "Filter"),
            e(
              Dropdown,
              { align: "end" },
              e(
                Dropdown.Toggle,
                {
                  className:
                    "mm-dropdown-btn w-100 d-flex align-items-center justify-content-between",
                },
                e(
                  "span",
                  { className: "d-flex align-items-center gap-2" },
                  e(Filter, { size: 16 }),
                  "All"
                ),
                e(ChevronDown, { size: 16 })
              ),
              e(
                Dropdown.Menu,
                { className: "mm-dd-menu w-100" },
                e(
                  Dropdown.Item,
                  { active: true, onClick: () => {} },
                  "All"
                )
              )
            )
          ),

          // meta
          e(
            Col,
            { md: 12, className: "text-end" },
            e(
              "div",
              { className: "text-muted small" },
              "Showing ",
              e(
                "span",
                { className: "fw-bold" },
                filteredRows.length === 0 ? 0 : startIndex + 1,
                "-",
                Math.min(startIndex + ITEMS_PER_PAGE, filteredRows.length)
              ),
              " of ",
              e("span", { className: "fw-bold" }, filteredRows.length),
              " districts"
            )
          )
        )
      )
    ),

    // Table
    e(
      Card,
      { className: "mm-card" },
      e(
        Card.Body,
        { className: "p-0" },
        districtLoading
          ? e(
              "div",
              { className: "text-center py-5" },
              e(Spinner, { animation: "border" }),
              e("div", { className: "mt-2 text-muted" }, "Loading districts...")
            )
          : e(
              React.Fragment,
              null,
              e(
                "div",
                { className: "mm-table-wrap" },
                e(
                  Table,
                  { hover: true, className: "mm-table mb-0" },
                  e(
                    "thead",
                    null,
                    e(
                      "tr",
                      null,
                      e(
                        "th",
                        {
                          style: { cursor: "pointer" },
                          onClick: () =>
                            setSortBy((p) =>
                              p === "dist_name_asc" ? "dist_name_desc" : "dist_name_asc"
                            ),
                        },
                        e(
                          "div",
                          { className: "d-flex align-items-center gap-1" },
                          "District ",
                          sortBy === "dist_name_asc" ? e(ChevronUp, { size: 16 }) : null,
                          sortBy === "dist_name_desc" ? e(ChevronDown, { size: 16 }) : null
                        )
                      ),
                      e("th", { style: { width: 160, textAlign: "center" } }, "Status"),
                      e(
                        "th",
                        { className: "mm-actions-th", style: { width: 180, textAlign: "right" } },
                        "Actions"
                      )
                    )
                  ),
                  e(
                    "tbody",
                    null,
                    ...(paginatedRows || []).map((r) =>
                      e(
                        "tr",
                        { key: r.dist_id },
                        e(
                          "td",
                          {
                            className: "fw-bold",
                            style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
                            title: r.dist_name,
                          },
                          r.dist_name
                        ),
                        e("td", { className: "text-center" }, e(StatusPill, { status: r.status })),
                        e(
                          "td",
                          { className: "mm-actions-td", style: { textAlign: "right" } },
                          e(
                            "div",
                            { className: "d-inline-flex gap-2" },
                            e(
                              Button,
                              {
                                variant: "light",
                                className: "mm-icon-btn primary",
                                onClick: () => openEdit(r),
                                disabled: districtSaving,
                                title: "Edit",
                              },
                              e(Edit2, { size: 16 })
                            ),
                            e(
                              Button,
                              {
                                variant: "light",
                                className: "mm-icon-btn danger",
                                onClick: () => onDelete(r),
                                disabled: districtDeleting,
                                title: "Delete",
                              },
                              e(Trash2, { size: 16 })
                            )
                          )
                        )
                      )
                    ),
                    !paginatedRows?.length
                      ? e(
                          "tr",
                          null,
                          e(
                            "td",
                            { colSpan: 3, className: "p-5 text-center" },
                            e(
                              "div",
                              null,
                              e(
                                "div",
                                { className: "mm-empty-icon mx-auto mb-2" },
                                e(Search, { size: 22 })
                              ),
                              e(
                                "div",
                                { className: "fw-bold mb-1" },
                                search ? "No results found" : "No districts found"
                              ),
                              e(
                                "div",
                                { className: "text-muted" },
                                search
                                  ? "No districts match your search."
                                  : "Get started by adding your first district."
                              )
                            )
                          )
                        )
                      : null
                  )
                )
              ),

              totalPages > 1
                ? e(
                    "div",
                    { className: "d-flex justify-content-center py-3 border-top mm-footer" },
                    e(Pagination, { className: "mb-0 master-pagination" }, getPaginationItems())
                  )
                : null
            )
      )
    ),

    // Modal
    e(
      Modal,
      {
        show: !!(modalOpen && modalType === "district"),
        onHide: () => dispatch(closeDtModal()),
        centered: true,
        className: "mm-modal",
      },
      e(
        Form,
        { onSubmit: (ev) => (ev.preventDefault(), onSave()) },
        e(
          Modal.Header,
          { closeButton: true },
          e(
            Modal.Title,
            { className: "fw-bold" },
            modalMode === "add" ? "Add District" : "Edit District"
          )
        ),
        e(
          Modal.Body,
          null,
          e(
            Row,
            { className: "g-3" },
            e(
              Col,
              { md: 12 },
              e(Form.Label, { className: "fw-bold" }, "District Name *"),
              e(Form.Control, {
                className: "mm-input",
                value: form.dist_name || "",
                onChange: (ev) => dispatch(setDtForm({ dist_name: ev.target.value })),
                placeholder: "Ex: Udupi",
                autoFocus: true,
              })
            ),
            e(
              Col,
              { md: 12 },
              e(Form.Label, { className: "fw-bold" }, "Status"),
              e(
                Form.Select,
                {
                  className: "mm-select",
                  value: Number(form.status) === 1 ? 1 : 0,
                  onChange: (ev) => dispatch(setDtForm({ status: Number(ev.target.value) })),
                },
                e("option", { value: 1 }, "Active"),
                e("option", { value: 0 }, "Inactive")
              )
            )
          )
        ),
        e(
          Modal.Footer,
          null,
          e(
            Button,
            {
              variant: "outline-secondary",
              className: "mm-cancel-btn",
              onClick: () => dispatch(closeDtModal()),
              disabled: districtSaving,
            },
            "Cancel"
          ),
          e(
            Button,
            {
              type: "submit",
              className: "mm-primary-btn",
              onClick: onSave,
              disabled: districtSaving,
            },
            districtSaving ? "Saving..." : "Save"
          )
        )
      )
    )
  );
}
